"use client"

import { ArrowDownIcon, MagnifyingGlassIcon } from "@heroicons/react/16/solid"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import ListItem from "@src/components/ListItem"
import { motion, useReducedMotion } from "framer-motion"
import { useEffect, useState } from "react"
import { enterTransition, ITEM_STAGGER, ITEMS_DELAY } from "./landingMotion"

const tokens = {
  BTC: { symbol: "BTC", icon: "/static/icons/token/btc.svg" },
  ETH: { symbol: "ETH", icon: "/static/icons/network/ethereum.svg" },
  NEAR: { symbol: "NEAR", icon: "/static/icons/token/near.svg" },
  SOL: { symbol: "SOL", icon: "/static/icons/network/solana.svg" },
  USDC: { symbol: "USDC", icon: "/static/icons/token/usdc.svg" },
  USDT: { symbol: "USDT", icon: "/static/icons/token/usdt.svg" },
} as const

type TokenSymbol = keyof typeof tokens

type HistoryEntry =
  | {
      id: string
      type: "swap"
      fromToken: TokenSymbol
      fromAmount: string
      toToken: TokenSymbol
      toAmount: string
      usdValue: string
      daysAgo: number
    }
  | {
      id: string
      type: "send" | "receive"
      token: TokenSymbol
      amount: string
      usdValue: string
      daysAgo: number
    }

const historyEntries: readonly HistoryEntry[] = [
  {
    id: "swap-usdc-btc",
    type: "swap",
    fromToken: "USDC",
    fromAmount: "12,500",
    toToken: "BTC",
    toAmount: "0.1959",
    usdValue: "$12,500",
    daysAgo: 0,
  },
  {
    id: "receive-usdt",
    type: "receive",
    token: "USDT",
    amount: "8,400",
    usdValue: "$8,400",
    daysAgo: 0,
  },
  {
    id: "swap-eth-near",
    type: "swap",
    fromToken: "ETH",
    fromAmount: "6.25",
    toToken: "NEAR",
    toAmount: "3,150",
    usdValue: "$20,000",
    daysAgo: 1,
  },
  {
    id: "send-eth",
    type: "send",
    token: "ETH",
    amount: "2.75",
    usdValue: "$8,800",
    daysAgo: 2,
  },
  {
    id: "swap-usdc-sol",
    type: "swap",
    fromToken: "USDC",
    fromAmount: "24,000",
    toToken: "SOL",
    toAmount: "377.3",
    usdValue: "$24,000",
    daysAgo: 3,
  },
  {
    id: "receive-eth",
    type: "receive",
    token: "ETH",
    amount: "1.5",
    usdValue: "$4,800",
    daysAgo: 4,
  },
  {
    id: "send-usdt",
    type: "send",
    token: "USDT",
    amount: "7,500",
    usdValue: "$7,500",
    daysAgo: 5,
  },
  {
    id: "swap-near-usdc",
    type: "swap",
    fromToken: "NEAR",
    fromAmount: "780",
    toToken: "USDC",
    toAmount: "4,953",
    usdValue: "$4,953",
    daysAgo: 7,
  },
]

export function HistoryHeader() {
  return (
    <header className="pointer-events-none flex items-center justify-between gap-4 pb-5">
      <h2 className="flex h-9 items-center font-medium text-gray-900 text-xl/7 tracking-tight">
        History
      </h2>

      <div className="-outline-offset-1 flex h-9 w-48 shrink-0 items-center gap-2 rounded-lg bg-gray-50 px-3 outline outline-gray-150">
        <MagnifyingGlassIcon className="size-4 shrink-0 text-gray-500" />
        <span className="font-medium text-gray-500 text-sm/5">
          Search activity
        </span>
      </div>
    </header>
  )
}

export function HistoryContent() {
  const [today, setToday] = useState<Date | null>(null)
  const reduceMotion = useReducedMotion()
  const animateEnter = !reduceMotion

  useEffect(() => {
    setToday(new Date())
  }, [])

  return (
    <div className="pointer-events-none space-y-1">
      {historyEntries.map((entry, index) => (
        <motion.div
          key={entry.id}
          {...enterTransition(ITEMS_DELAY + index * ITEM_STAGGER, animateEnter)}
        >
          <HistoryPreviewRow entry={entry} today={today} />
        </motion.div>
      ))}
    </div>
  )
}

export default function LandingHistoryPreview() {
  return (
    <section className="mx-auto h-full max-w-lg overflow-hidden p-8">
      <HistoryHeader />
      <HistoryContent />
    </section>
  )
}

function HistoryPreviewRow({
  entry,
  today,
}: {
  entry: HistoryEntry
  today: Date | null
}) {
  const date = formatHistoryDate(entry.daysAgo, today)

  if (entry.type === "swap") {
    const fromToken = tokens[entry.fromToken]
    const toToken = tokens[entry.toToken]

    return (
      <ListItem>
        <ListItem.Icon
          badgeIcon={
            <AssetComboIcon icon={fromToken.icon} sizeClassName="size-full" />
          }
        >
          <AssetComboIcon icon={toToken.icon} sizeClassName="size-full" />
        </ListItem.Icon>

        <ListItem.Content>
          <ListItem.Subtitle>
            {entry.fromAmount} {fromToken.symbol}
          </ListItem.Subtitle>
          <ListItem.Title>
            +{entry.toAmount} {toToken.symbol}
          </ListItem.Title>
        </ListItem.Content>

        <ListItem.Content align="end">
          <ListItem.Subtitle>{date}</ListItem.Subtitle>
          <ListItem.Title>{entry.usdValue}</ListItem.Title>
        </ListItem.Content>
      </ListItem>
    )
  }

  const token = tokens[entry.token]
  const isReceive = entry.type === "receive"

  return (
    <ListItem>
      <ListItem.Icon
        badgeIcon={
          <div
            className={
              isReceive
                ? "flex items-center justify-center rounded-full bg-green-600"
                : "flex items-center justify-center rounded-full bg-sky-400"
            }
          >
            <ArrowDownIcon
              className={
                isReceive
                  ? "size-4.5 text-white"
                  : "size-4.5 rotate-225 text-white"
              }
            />
          </div>
        }
      >
        <AssetComboIcon icon={token.icon} sizeClassName="size-full" />
      </ListItem.Icon>

      <ListItem.Content>
        <ListItem.Subtitle>{isReceive ? "Receive" : "Send"}</ListItem.Subtitle>
        <ListItem.Title>
          {entry.amount} {token.symbol}
        </ListItem.Title>
      </ListItem.Content>

      <ListItem.Content align="end">
        <ListItem.Subtitle>{date}</ListItem.Subtitle>
        <ListItem.Title>{entry.usdValue}</ListItem.Title>
      </ListItem.Content>
    </ListItem>
  )
}

function formatHistoryDate(daysAgo: number, today: Date | null) {
  if (today == null) return "\u00a0"
  if (daysAgo === 0) return "Today"
  if (daysAgo === 1) return "Yesterday"

  const date = new Date(today)
  date.setHours(12, 0, 0, 0)
  date.setDate(date.getDate() - daysAgo)

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date)
}
